import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { lastFiveContract, getAllContracts } from "../../ReduxStore/Slice/Contract/contractSlice";
import { getAllQuotes } from "../../ReduxStore/Slice/Quote/CreateQuoteSlice";

// NOTE: import these thunks yourself (you said you'll add them):
// import { lastFiveQuote } from "../../ReduxStore/Slice/Quote/CreateQuoteSlice";
// import { lastFiveContract } from "../../ReduxStore/Slice/Contract/contractSlice";

const formatCurrency = (val) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(val || 0);
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Accept':
    case 'Signed':
      return 'accept'; // Using the existing 'accept' class (green)
    case 'Pending':
    case 'Awaiting Signature':
      return 'pending'; // Assuming a 'pending' class (yellow) - need to ensure this class exists
    case 'Reject':
      return 'reject'; // Using the existing 'reject' class (red)
    case 'Sent':
      return 'sent'; // Assuming a 'sent' class (blue) - need to ensure this class exists
    default:
      return '';
  }
};

const PAGE_SIZE = 10; // per page records for infinite scroll

export default function QuoteReport() {
  const dispatch = useDispatch();
  const agent_id = JSON.parse(localStorage.getItem("agent_id"));

  const [activeTab, setActiveTab] = useState("Quote");
  const [statusValue, setStatusValue] = useState("All");
  const [expandedId, setExpandedId] = useState(null);

  // Search sent to the API (req.search)
  const [InputValue, setInputValue] = useState("");

  // Data + pagination + loader
  const [lastFiveQuoteData, setLastFiveQuoteData] = useState([]);
  const [lastFiveContractData, setLastFiveContractData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // Rows currently shown depend on the active tab
  const tableData = activeTab === "Quote" ? lastFiveQuoteData : lastFiveContractData;

  // ---------------- API CALLS ----------------
  const getLastFiveQuote = async () => {
    setLoading(true);
    const req = {
      agent_id,
      page: currentPage,
      limit: PAGE_SIZE,
      search: InputValue,
      quoteTiles: "",
      type: "reports"
    };
    await dispatch(getAllQuotes(req)).unwrap()
      .then((response) => {
        if (response.status) {
          setLastFiveQuoteData((prev) =>
            currentPage === 1 ? response.data : [...prev, ...response.data]
          );
          setTotalRecords(response?.pagination?.total || 0);
        } else if (currentPage === 1) {
          setLastFiveQuoteData([]);
          setTotalRecords(0);
        }
      })
      .catch((error) => {
        console.error("Error fetching quote data:", error);
      })
      .finally(() => setLoading(false));
  };

  const getLastFiveContract = async () => {
    setLoading(true);
    const req = {
      agent_id,
      page: currentPage,
      limit: PAGE_SIZE,
      search: InputValue,
      contractTiles: "",
      type: "reports"
    };
    await dispatch(getAllContracts(req)).unwrap()
      .then((response) => {
        if (response.status) {
          setLastFiveContractData((prev) =>
            currentPage === 1 ? response.data : [...prev, ...response.data]
          );
          setTotalRecords(response?.pagination?.total || 0);
        } else if (currentPage === 1) {
          setLastFiveContractData([]);
          setTotalRecords(0);
        }
      })
      .catch((error) => {
        console.error("Error fetching contract data:", error);
      })
      .finally(() => setLoading(false));
  };

  // Fetch whenever the tab, page (scroll) or search changes
  useEffect(() => {
    if (activeTab === "Quote") getLastFiveQuote();
    else getLastFiveContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, InputValue]);

  // Switch tab -> reset everything and load fresh (page 1) for that tab
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setStatusValue("All");
    setInputValue("");
    setExpandedId(null);
    setCurrentPage(1);
    setLastFiveQuoteData([]);
    setLastFiveContractData([]);
  };

  // Search -> always restart from page 1
  const handleSearch = (value) => {
    setInputValue(value);
    setCurrentPage(1);
  };

  // Infinite scroll: near the bottom -> load next page (10 more)
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      !loading &&
      scrollHeight - scrollTop - clientHeight < 60 &&
      tableData.length < totalRecords
    ) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const toggleQuote = (PackageServices, id) => {
    if (PackageServices?.length === 0) return; // matches original: no expanded row available for these
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const summaryData = useMemo(() => {
    const list = tableData || [];
    const net = list.reduce((acc, curr) => acc + (Number(curr.netValue) || 0), 0);
    const discount = list.reduce((acc, curr) => acc + (Number(curr.discountValue) || 0), 0);
    const total = list.reduce((acc, curr) => acc + (Number(curr.totalValue) || 0), 0);

    return {
      entries: totalRecords || list.length,
      net: formatCurrency(net),
      discount: formatCurrency(discount),
      total: formatCurrency(total),
    };
  }, [tableData, totalRecords]);

  return (
    <div className="quote-report-wrapper">
      <div className="quote-report-container">
        {/* HEADER */}
        <div className="report-header">
          <div className="report-heading-row">
            <div>
              <div className="report-label">
                <i className="bi bi-file-earmark-bar-graph"></i>
                REPORTS
              </div>

              <h1 className="report-title">{activeTab} Report</h1>

              <p className="report-description">
                Expand a row to see services, payment types, contract types and discounts.
              </p>

              <div className="report-tabs">
                <button
                  className={`report-tab${activeTab === "Quote" ? " active" : ""}`}
                  onClick={() => handleTabChange("Quote")}
                >
                  Quote
                </button>
                <button
                  className={`report-tab${activeTab === "Contract" ? " active" : ""}`}
                  onClick={() => handleTabChange("Contract")}
                >
                  Contract
                </button>
              </div>
            </div>

            {/* EXPORT */}
            <div className="export-buttons">
              <button className="export-btn">
                <i className="bi bi-file-earmark-text"></i>
                CSV
                <i className="bi bi-download"></i>
              </button>

              <button className="export-btn">
                <i className="bi bi-file-earmark-spreadsheet"></i>
                Excel
                <i className="bi bi-download"></i>
              </button>

              <button className="export-btn">
                <i className="bi bi-file-earmark-pdf"></i>
                PDF
                <i className="bi bi-download"></i>
              </button>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="summary-grid">
          <div className="summary-card-new">
            <div className="summary-label-new">Entries</div>
            <div className="summary-value-new">{summaryData.entries}</div>
          </div>

          <div className="summary-card-new">
            <div className="summary-label-new">Net</div>
            <div className="summary-value-new">{summaryData.net}</div>
          </div>

          <div className="summary-card-new discount">
            <div className="summary-label-new">Discount</div>
            <div className="summary-value-new">{summaryData.discount}</div>
          </div>

          <div className="summary-card-new total">
            <div className="summary-label-new">Total (Inc. VAT)</div>
            <div className="summary-value-new">{summaryData.total}</div>
          </div>
        </div>

        {/* SEARCH */}
        <div className="search-filter-bar">
          <div className="search-box-new">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Search client, reference, agent, service..."
              value={InputValue}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <select
            className="status-filter-new"
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
          >
            <option value="All">All {activeTab.toLowerCase()} statuses</option>
            {activeTab === "Quote" ? (
              <>
                <option value="Accept">Accept</option>
                <option value="Pending">Pending</option>
                <option value="Reject">Reject</option>
              </>
            ) : (
              <>
                <option value="Signed">Signed</option>
                <option value="Awaiting Signature">Awaiting Signature</option>
                <option value="Sent">Sent</option>
              </>
            )}
          </select>
        </div>

        {/* TABLE */}
        <div className="report-table-wrapper" style={{ position: "relative" }}>
          {/* Full loader on first load (no rows yet) */}
          {loading && tableData.length === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.65)",
                zIndex: 5,
              }}
            >
              <div className="loader"></div>
            </div>
          )}

          <div className="report-table-scroll" onScroll={handleScroll}>
            <table className="quote-table">
              <thead>
                <tr>
                  <th className="expand-cell"></th>
                  <th>Reference</th>
                  <th>Client</th>
                  <th>
                    Agent /<br />Manager
                  </th>
                  <th>Package</th>
                  <th>
                    Contract<br />Type
                  </th>
                  <th>Services</th>
                  <th className="text-end">Net</th>
                  <th className="text-end">Discount</th>
                  <th className="text-end">
                    Total<br />(Inc. VAT)
                  </th>
                  <th>
                    {activeTab}<br />Status
                  </th>
                </tr>
              </thead>

              <tbody>
                {tableData.map((quote, index) => {

                  console.log("row ", quote);
                  let id = quote.id;

                  let EnterDefaultValues = quote?.QuoteEnterDefaultValues;
                  let Individuals = quote?.QuoteIndividuals;
                  let PackageServices = quote?.QuotePackageServices;

                  const isExpanded = expandedId === id;
                  return (
                    <React.Fragment key={id ?? index}>
                      <tr
                        className={`quote-main-row${isExpanded ? " expanded" : ""}${PackageServices?.length === 0 ? " collapsed-row" : ""
                          }`}
                      >
                        <td className="expand-cell">
                          <button
                            className="expand-btn"
                            onClick={() => toggleQuote(PackageServices, id)}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </td>

                        <td>
                          <div className="reference">{id}</div>
                        </td>

                        <td>
                          <div className="client-name">{quote?.Client?.client_name}</div>
                          <div className="muted-line">{quote?.Client?.business_type || "Dummy"}</div>
                        </td>

                        <td>
                          <div className="agent-name">{quote?.Agent?.agent_name}</div>
                          <div className="manager">Mgr: {quote?.Agent?.maanager || "Dummy"}</div>
                        </td>

                        <td>
                          <div className="package">
                            {(() => {
                              const hasNullPackage = PackageServices?.some(
                                (item) => item?.package_id === null
                              );

                              if (hasNullPackage) {
                                return "Custom Package";
                              }

                              const packageItem = PackageServices?.find((item) => item?.Package);
                              return packageItem?.Package?.name || "-";
                            })()}
                          </div>
                        </td>

                        <td>
                          <div className="contract-tags">
                            {(quote.contractTags || []).map((tag) => (
                              <span className="contract-tag" key={tag}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>

                        <td>
                          <span className="service-count">{quote.servicesCount}</span>
                        </td>

                        <td className="amount-new">{formatCurrency(quote.netValue)}</td>

                        <td className="discount-value">{quote.discountValue > 0 ? `-${formatCurrency(quote.discountValue)}` : "—"}</td>

                        <td className="amount-new">{formatCurrency(quote.totalValue)}</td>

                        <td>
                          <span className={`status ${getStatusColor(quote.currentStatus)}`}>
                            {quote.currentStatus}
                          </span>
                        </td>
                      </tr>

                      {/* EXPANDED ROW */}
                      {quote.details && (
                        <tr
                          className={`expanded-content${isExpanded ? " show" : ""}`}
                          style={{
                            display: isExpanded ? "table-row" : "none",
                          }}
                        >
                          <td colSpan={11}>
                            <div className="expanded-inner">
                              <div className="expanded-grid">
                                {/* LEFT */}
                                <div>
                                  <div className="breakdown-heading">
                                    Services Breakdown
                                  </div>
                                  {/* SERVICES */}
                                  <div className="service-box">
                                    <table className="service-table">
                                      <thead>
                                        <tr>
                                          <th>Service</th>
                                          <th>Payment Type</th>
                                          <th>Contract Type</th>
                                          <th className="text-end">Amount</th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {quote.details.services.map((service) => (
                                          <tr key={service.name}>
                                            <td className="service-name">{service.name}</td>
                                            <td>
                                              <span className="payment-badge">
                                                {service.payment}
                                              </span>
                                            </td>
                                            <td>
                                              <span className="type-badge">{service.type}</span>
                                            </td>
                                            <td className="text-end">{service.amount}</td>
                                          </tr>
                                        ))}

                                        <tr className="subtotal-row">
                                          <td colSpan={3}>Subtotal (Gross)</td>
                                          <td className="text-end">
                                            {quote.details.subtotalGross}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* DISCOUNT */}
                                  <div className="discount-box">
                                    <div>
                                      <div className="discount-title">
                                        Discount (Applied once to the entry)
                                      </div>

                                      <div className="discount-description">
                                        <strong className="discount-description-percentage">{quote.details.discountBox.percent}</strong> on
                                        gross{" "}
                                        <strong>{quote.details.discountBox.gross}</strong> ·
                                        Approver:{" "}
                                        <strong>{quote.details.discountBox.approver}</strong>
                                      </div>
                                    </div>

                                    <div className="discount-amount">
                                      <div className="discount-amount-label">
                                        Discount Amount
                                      </div>

                                      <div className="discount-amount-value">
                                        {quote.details.discountBox.amount}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* RIGHT */}
                                <div>
                                  {/* MULTIPLIERS */}
                                  <div className="side-card">
                                    <div className="side-card-title">Multipliers</div>
                                    {quote.details.multipliers.map((m) => (
                                      <div className="side-row" key={m.label}>
                                        <span>{m.label}</span>
                                        <span>{m.value}</span>
                                      </div>
                                    ))}
                                  </div>

                                  {/* TOTALS */}
                                  <div className="side-card">
                                    <div className="side-card-title">Totals</div>
                                    {quote.details.totals.map((t) => (
                                      <div className="side-row" key={t.label}>
                                        <span>{t.label}</span>
                                        <span>{t.value}</span>
                                      </div>
                                    ))}

                                    <div className="side-row total-row">
                                      <span>{quote.details.totalRow.label}</span>
                                      <span>{quote.details.totalRow.value}</span>
                                    </div>
                                  </div>

                                  {/* SALES */}
                                  <div className="side-card">
                                    <div className="side-card-title">Sales & Contract</div>
                                    {quote.details.sales.map((s) => (
                                      <div className="side-row" key={s.label}>
                                        <span>{s.label}</span>
                                        <span>{s.value}</span>
                                      </div>
                                    ))}
                                  </div>

                                  {/* NOTES */}
                                  <div className="side-card">
                                    <div className="side-card-title">Notes</div>
                                    <div className="notes-text">{quote.details.notes}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Bottom loader while fetching the next page on scroll */}
            {loading && tableData.length > 0 && (
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
                <div className="loader"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
